import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import type { TelemetryPoint } from "@/lib/physics";
import { Panel } from "@/components/ui";

const GRID_COLOR = "#1c2533";
const AXIS_COLOR = "#5b6674";
const TOOLTIP_STYLE = {
  backgroundColor: "#0b1018",
  border: "1px solid #232e3e",
  color: "#e8edf6",
  fontSize: 11,
  borderRadius: 2,
  padding: 6,
};

function CommonAxes() {
  return (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
      <XAxis
        dataKey="t"
        tick={{ fontSize: 10, fill: AXIS_COLOR }}
        axisLine={false}
        tickMargin={8}
        label={{ value: "Time (s)", position: "insideBottomRight", offset: 4, fontSize: 10, fill: AXIS_COLOR }}
      />
      <YAxis tick={{ fontSize: 10, fill: AXIS_COLOR }} axisLine={false} tickMargin={8} />
      <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ strokeDasharray: "3 3", stroke: "#334155" }} />
      <Legend verticalAlign="top" align="right" height={30} wrapperStyle={{ outline: "none" }} />
    </>
  );
}

function ChartPanel({
  title,
  subtitle,
  height,
  children,
}: {
  title: string;
  subtitle?: string;
  height: number;
  children: any;
}) {
  return (
    <Panel title={title} padded={false}>
      <div className="p-3">
        {subtitle && <p className="text-[0.625rem] text-dim mb-2">{subtitle}</p>}
        <div style={{ height: `${height}px` }}>{children}</div>
      </div>
    </Panel>
  );
}

interface FlightChartsProps {
  telemetry: readonly TelemetryPoint[];
  currentIndex: number;
  height?: number;
}

export function FlightCharts({
  telemetry,
  currentIndex,
  height = 200,
}: FlightChartsProps) {
  const visible =
    currentIndex >= 0 && telemetry.length > 0
      ? telemetry.slice(0, currentIndex + 1)
      : [];

  if (visible.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="panel p-3 h-[200px] flex flex-col items-center justify-center text-muted text-sm">
          <p>No altitude telemetry</p>
        </div>
        <div className="panel p-3 h-[200px] flex flex-col items-center justify-center text-muted text-sm">
          <p>No velocity telemetry</p>
        </div>
        <div className="panel p-3 h-[200px] flex flex-col items-center justify-center text-muted text-sm">
          <p>No position telemetry</p>
        </div>
        <div className="panel p-3 h-[200px] flex flex-col items-center justify-center text-muted text-sm">
          <p>No fuel telemetry</p>
        </div>
        <div className="panel p-3 h-[200px] flex flex-col items-center justify-center text-muted text-sm">
          <p>No attitude telemetry</p>
        </div>
        <div className="panel p-3 h-[200px] flex flex-col items-center justify-center text-muted text-sm">
          <p>No control telemetry</p>
        </div>
        </div>
      );
    }

    const chartData = visible.map((p) => ({
      t: p.t,
    altitude: p.altitude,
    vz: p.vz,
    vx: p.vx,
    x: p.x,
    fuel: p.fuel,
    theta: (p.theta * 180) / Math.PI,
    throttle: p.throttle * 100,
    gimbal: p.gimbal,
  }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      <ChartPanel title="Altitude vs Time" subtitle="Vertical distance from launch pad" height={height}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={{ top: 16, right: 24, bottom: 26, left: 0 }}>
            {CommonAxes()}
            <Line type="monotone" dataKey="altitude" stroke="#22d3ee" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-2 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-cyan-400" />
          <span className="text-xs text-muted">Altitude (m)</span>
        </div>
      </ChartPanel>

      <ChartPanel title="Vertical Velocity vs Time" subtitle="Descent rate (negative = descending)" height={height}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={{ top: 16, right: 24, bottom: 26, left: 0 }}>
            {CommonAxes()}
            <Line type="monotone" dataKey="vz" stroke="#3b82f6" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-2 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />
          <span className="text-xs text-muted">Vertical velocity (m/s)</span>
        </div>
      </ChartPanel>

      <ChartPanel title="Horizontal Position vs Time" subtitle="Downrange distance from target" height={height}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={{ top: 16, right: 24, bottom: 26, left: 0 }}>
            {CommonAxes()}
            <Line type="monotone" dataKey="x" stroke="#a855f7" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-2 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-purple-400" />
          <span className="text-xs text-muted">Horizontal position (m)</span>
        </div>
      </ChartPanel>

      <ChartPanel title="Fuel Remaining vs Time" subtitle="Propellant mass" height={height}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={{ top: 16, right: 24, bottom: 26, left: 0 }}>
            {CommonAxes()}
            <Line type="monotone" dataKey="fuel" stroke="#f59e0b" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-2 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="text-xs text-muted">Fuel (kg)</span>
        </div>
      </ChartPanel>

      <ChartPanel title="Pitch Angle vs Time" subtitle="Degrees from vertical" height={height}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={{ top: 16, right: 24, bottom: 26, left: 0 }}>
            {CommonAxes()}
            <Line type="monotone" dataKey="theta" stroke="#ec4899" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-2 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-pink-400" />
          <span className="text-xs text-muted">Pitch (degrees)</span>
        </div>
      </ChartPanel>

      <ChartPanel title="Throttle & Gimbal vs Time" subtitle="Engine commands" height={height}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={{ top: 16, right: 24, bottom: 26, left: 0 }}>
            {CommonAxes()}
            <Line type="monotone" dataKey="throttle" stroke="#22d3ee" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="gimbal" stroke="#fbbf24" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-2 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-cyan-400" />
          <span className="text-xs text-muted">Throttle (%)</span>
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="text-xs text-muted">Gimbal (rad)</span>
        </div>
      </ChartPanel>
    </div>
  );
}
